import { useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { PasswordResetRequestDTO } from '@/core/api/dtos/request/auth/passwordReset'
import { useAuth } from '@/core/hooks/useAuth'

import { SetupUserPageUI } from './SetupUserPageUI'

interface SetupUserFormData {
	password: string
	password_confirmation: string
}

export const SetupUserPage = () => {
	const { email, token } = useParams<{ email: string; token: string }>()
	const { useResetPassword, login } = useAuth()
	const { t } = useTranslation('auth')
	const [apiError, setApiError] = useState<string | null>(null)

	// Validation schema
	const validationSchema = yup.object().shape({
		password: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.password').toLowerCase(),
				})
			)
			.min(8, t('register.errors.passwordMinLength')),
		password_confirmation: yup
			.string()
			.required(
				t('register.errors.required', {
					field: t('register.confirmPassword').toLowerCase(),
				})
			)
			.oneOf([yup.ref('password')], t('register.errors.passwordMismatch')),
	})

	const form = useForm<SetupUserFormData, any, SetupUserFormData>({
		resolver: yupResolver(validationSchema) as any,
		mode: 'onChange',
		reValidateMode: 'onChange',
	})

	const password = form.watch('password')

	const resetPasswordMutation = useResetPassword({
		options: {
			onSuccess: () => {
				login(
					{
						email: decodeURIComponent(email || ''),
						password: password,
					},
					'/zones'
				)
			},
		},
	})

	const handleSubmit = async (values: SetupUserFormData) => {
		if (!email || !token) {
			setApiError('Invalid setup link. Email or token is missing.')
			return
		}

		setApiError(null)

		const payload: PasswordResetRequestDTO = {
			email: decodeURIComponent(email),
			password: values.password,
			password_confirmation: values.password_confirmation,
			token: token,
		}

		resetPasswordMutation.mutate(payload)
	}

	return (
		<SetupUserPageUI
			isLoading={resetPasswordMutation.isPending}
			error={apiError}
			form={form}
			onFinish={handleSubmit}
		/>
	)
}
